import React, { useState, useRef } from 'react';
import './MultipleImageUpload.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { compressImages, formatFileSize, getCompressionRatio } from '../utils/imageCompression';

interface MultipleImageUploadProps {
  onImagesSelect: (images: (File | string)[]) => void;
  currentImages?: (File | string)[];
  disabled?: boolean;
  maxImages?: number;
}

interface SortableItemProps {
  id: string;
  image: File | string;
  index: number;
  onRemove: (index: number) => void;
  disabled: boolean;
  isCover: boolean;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, image, index, onRemove, disabled, isCover }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getPreviewUrl = (img: File | string): string => {
    if (typeof img === 'string') return img;
    return URL.createObjectURL(img);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`image-preview-item sortable-item ${isCover ? 'cover-photo' : ''}`}
      {...attributes}
    >
      <img
        src={getPreviewUrl(image)}
        alt={`Preview ${index + 1}`}
        {...listeners}
      />
      {isCover && <div className="cover-badge">Cover</div>}
      <div className="image-overlay">
        <div className="image-controls">
          <button
            type="button"
            className="remove-image"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Delete button clicked for index:', index);
              onRemove(index);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            disabled={disabled}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

const MultipleImageUpload: React.FC<MultipleImageUploadProps> = ({
  onImagesSelect,
  currentImages = [],
  disabled = false,
  maxImages = 5
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [images, setImages] = useState<(File | string)[]>(currentImages);
  console.log('MultipleImageUpload rendered with props:', {
    currentImages,
    disabled,
    maxImages,
    imagesLength: images.length,
    onImagesSelect: typeof onImagesSelect
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };

  const handleFiles = async (files: File[]) => {
    // Filter valid image files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`"${file.name}" is not an image file`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Check if adding these files would exceed the limit
    if (images.length + validFiles.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images. Please remove some existing images first.`);
      return;
    }

    try {
      // Compress images with progress indication
      console.log(`Compressing ${validFiles.length} image(s)...`);

      const compressedFiles = await compressImages(
        validFiles,
        {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
          format: 'webp'
        },
        (current, total) => {
          console.log(`Compressed ${current} of ${total} images`);
        }
      );

      // Log compression results
      validFiles.forEach((original, index) => {
        const compressed = compressedFiles[index];
        const ratio = getCompressionRatio(original.size, compressed.size);
        console.log(
          `${original.name}: ${formatFileSize(original.size)} → ${formatFileSize(compressed.size)} (${ratio}% reduction)`
        );
      });

      // Add compressed files to images array
      const updatedImages = [...images, ...compressedFiles];
      setImages(updatedImages);
      onImagesSelect(updatedImages);
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('Failed to compress images. Please try again.');
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onImagesSelect(updatedImages);
  };

  const clearAllImages = () => {
    setImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImagesSelect([]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((_, index) => `item-${index}` === active.id);
      const newIndex = images.findIndex((_, index) => `item-${index}` === over.id);

      const reorderedImages = arrayMove(images, oldIndex, newIndex);
      setImages(reorderedImages);
      onImagesSelect(reorderedImages);
    }
  };


  return (
    <div className="multiple-image-upload-container">
      <label className="image-upload-label">
        Trailer Images ({images.length}/{maxImages})
      </label>
      
      <div 
        className={`multiple-image-upload-area ${dragOver ? 'drag-over' : ''} ${disabled ? 'disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {images.length === 0 ? (
          <div className="upload-placeholder">
            <div className="upload-icon">📷</div>
            <div className="upload-text">
              <strong>Click to upload</strong> or drag and drop
            </div>
            <div className="upload-hint">
              PNG, JPG, WEBP up to 10MB each (max {maxImages} images)
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((_, index) => `item-${index}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="image-grid">
                {images.map((image, index) => (
                  <SortableItem
                    key={`item-${index}`}
                    id={`item-${index}`}
                    image={image}
                    index={index}
                    onRemove={removeImage}
                    disabled={disabled}
                    isCover={index === 0}
                  />
                ))}

                {images.length < maxImages && (
                  <div className="add-more-placeholder" onClick={(e) => { e.stopPropagation(); handleClick(); }}>
                    <div className="add-icon">+</div>
                    <div className="add-text">Add More</div>
                  </div>
                )}
              </div>
            </SortableContext>
          </DndContext>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled}
        />
      </div>
      
      {images.length > 0 && (
        <div className="image-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClick}
            disabled={disabled || images.length >= maxImages}
          >
            {images.length >= maxImages ? 'Maximum Reached' : 'Add More Images'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={clearAllImages}
            disabled={disabled}
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};

export default MultipleImageUpload;